// The single declarative navigation model ([NAV-NO-ROUTE-INPUT]).
//
// `buildNavModel` is a PURE function: given a masked-auth snapshot, the club
// hierarchy, and the visiting scope, it returns the full permitted `NavNode`
// tree. It takes NO route input by construction, so navigation VISIBILITY can
// never depend on the current route (the defect this whole effort fixes). The
// sidebar (PR 2) renders this tree; expansion and active-highlight are computed
// there from route + UI state and are display-only concerns.
//
// Lock state (`locked`) is intentionally left unset here. It depends on per-club
// subscription status, which is loaded lazily by `useClubNavContext` (PR 2b) and
// applied when club sub-items render (PR 3). Everything else - which nodes exist
// and which are permitted - is synchronous and lives in this function.

import {
  mdiAccountGroupOutline,
  mdiCalendarMonthOutline,
  mdiCogOutline,
  mdiCreditCardOutline,
  mdiCurrencyUsd,
  mdiDomain,
  mdiEmailOutline,
  mdiHome,
  mdiMagnify,
  mdiMapMarkerOutline,
  mdiNotebookOutline,
  mdiOfficeBuildingOutline,
  mdiSchoolOutline,
  mdiSync,
  mdiUploadOutline,
  mdiViewDashboardOutline,
  mdiWeb,
} from "@mdi/js";
import type { RouteLocationRaw } from "vue-router";
import { isEducationTabEnabledForClub } from "@/club/utils/educationFeature";
import { FORCED_PERSONAL_DASHBOARD_QUERY_PARAM } from "@/store/auth";
import { canManageClub, canManageDistrict, isClubOfficer, type NavAuthContext } from "./navPermissions";

type NavNodeKind = "section" | "district" | "division" | "area" | "club" | "link";

export interface NavNode {
  /** Stable, collision-free key, e.g. "district:91:cot", "club:acme". */
  id: string;
  kind: NavNodeKind;
  label: string;
  /** @mdi/js path string. */
  icon?: string;
  /** Present when the node is navigable. */
  to?: RouteLocationRaw;
  /** Active-state matching mode for the renderer; defaults to "prefix". */
  match?: "exact" | "prefix";
  /**
   * Route names that belong to this link's page family. The renderer marks the
   * link active on any of them, provided the route's `districtId` equals the
   * link's. Needed because RouterLink only matches the link's own route record,
   * and sibling routes such as `district-cot-management` are separate records.
   */
  activeRouteNames?: readonly string[];
  /**
   * Club sub-item links only: the item is hidden behind a subscription padlock
   * when the owning club's subscription is inactive (cancelled / not_subscribed).
   * The pure model only MARKS which items are lockable; the live lock/loading
   * display is resolved at render time from `useClubNavContext` ([NAV-LOCK-DISPLAY]),
   * because it depends on per-club subscription status loaded lazily on expand.
   */
  subscriptionLock?: boolean;
  /** True when the node has children the user can open. */
  expandable?: boolean;
  children?: NavNode[];
  /** Club nodes only: secondary line under the name (e.g. "Division B - Area 6"). */
  locationLabel?: string;
  /**
   * The numeric id of the club this node pertains to. On a club node it is the
   * club's own id (for route-independent active detection); on a lockable club
   * sub-item link it is the owning club's id, so the renderer can resolve that
   * club's subscription snapshot for the padlock.
   */
  clubId?: number;
  /** Club nodes only: canonical short name, for route-independent active detection. */
  clubShortName?: string;
}

// --- Structural inputs (compatible with useUserClubHierarchy's DistrictGroup) ---

export interface NavClubInput {
  id: number;
  shortName: string;
  name: string;
  locationLabel?: string;
  /** Undefined is treated as hosted (member clubs from the hierarchy omit it). */
  isOnToastHost?: boolean;
}

interface NavDivisionInput {
  id: string;
  areas: { id: string }[];
}

export interface NavDistrictInput {
  id: string;
  divisions: NavDivisionInput[];
  clubs: NavClubInput[];
}

interface NavModelOptions {
  /** The public subscription status, always enabled (WorkspaceSidebar `isSubscriptionEnabled`). */
  subscriptionFeatureEnabled?: boolean;
  /**
   * When true, the "Home" link carries `?personalDashboard=1` so the
   * auth guard keeps inactive-managed-club users on the personal dashboard
   * instead of bouncing them back to a club workspace. Mirrors WorkspaceSidebar
   * `dashboardHomeLink` (`isInactiveManagedClubPersonalDashboardEligible`).
   */
  forcePersonalDashboard?: boolean;
}

export interface BuildNavModelInputs {
  auth: NavAuthContext;
  /** Member-club hierarchy, e.g. hierarchy.buildDistrictGroups({}). */
  districts: NavDistrictInput[];
  /** Visiting district group, or null when not visiting. */
  visitingDistrict: NavDistrictInput | null;
  /** The single visiting club, or null. */
  visitingClub: NavClubInput | null;
  options?: NavModelOptions;
}

// --- Club sub-items (mirrors WorkspaceSidebar `clubNavItems`, for ANY club) ---

interface ClubItemSpec {
  key: string;
  label: string;
  icon: string;
  routeName: string;
  match?: "exact" | "prefix";
  requiresManagement?: boolean;
  /**
   * Subject to the subscription padlock when the club is inactive. Mirrors the
   * pre-refactor `restrictedWhenInactive` flag: the management group + Meetings
   * lock; Home / Dashboard / Subscription never do.
   */
  restrictedWhenInactive?: boolean;
}

function clubChildren(auth: NavAuthContext, club: NavClubInput, idPrefix: string, options: NavModelOptions): NavNode[] {
  const shortName = club.shortName;
  const specs: ClubItemSpec[] = [
    { key: "home", label: "Home", icon: mdiHome, routeName: "ClubHome", match: "exact" },
    { key: "dashboard", label: "Dashboard", icon: mdiViewDashboardOutline, routeName: "ClubDCPDashboard" },
  ];

  // Non-hosted clubs expose only Home + Dashboard (WorkspaceSidebar early return).
  if (club.isOnToastHost === false) {
    return specs.map((s) => clubItemNode(club, idPrefix, s));
  }

  specs.push({
    key: "meetings",
    label: "Meetings",
    icon: mdiCalendarMonthOutline,
    routeName: "ClubMeetings",
    restrictedWhenInactive: true,
  });

  if (
    isEducationTabEnabledForClub({ clubId: club.id, isOfficer: isClubOfficer(auth, shortName), isAdmin: auth.isAdmin })
  ) {
    specs.push({
      key: "education",
      label: "Education",
      icon: mdiSchoolOutline,
      routeName: "ClubEducation",
      requiresManagement: true,
      restrictedWhenInactive: true,
    });
  }

  specs.push(
    {
      key: "membership",
      label: "Membership",
      icon: mdiAccountGroupOutline,
      routeName: "ClubMembership",
      requiresManagement: true,
      restrictedWhenInactive: true,
    },
    {
      key: "guestbook",
      label: "Guestbook",
      icon: mdiNotebookOutline,
      routeName: "GuestbookManagement",
      requiresManagement: true,
      restrictedWhenInactive: true,
    },
    {
      key: "website",
      label: "Website",
      icon: mdiWeb,
      routeName: "WebsiteConfig",
      requiresManagement: true,
      restrictedWhenInactive: true,
    },
    {
      key: "finance",
      label: "Finance",
      icon: mdiCurrencyUsd,
      routeName: "ClubFinance",
      requiresManagement: true,
      restrictedWhenInactive: true,
    },
    {
      key: "settings",
      label: "Settings",
      icon: mdiCogOutline,
      routeName: "ClubSettings",
      requiresManagement: true,
      restrictedWhenInactive: true,
    },
  );

  if (options.subscriptionFeatureEnabled) {
    specs.push({
      key: "subscription",
      label: "Subscription",
      icon: mdiCreditCardOutline,
      routeName: "ClubSubscription",
      requiresManagement: true,
    });
  }

  return specs
    .filter((s) => (s.requiresManagement ? canManageClub(auth, shortName) : true))
    .map((s) => clubItemNode(club, idPrefix, s));
}

function clubItemNode(club: NavClubInput, idPrefix: string, spec: ClubItemSpec): NavNode {
  const node: NavNode = {
    id: `${idPrefix}:${spec.key}`,
    kind: "link",
    label: spec.label,
    icon: spec.icon,
    to: { name: spec.routeName, params: { clubShortName: club.shortName } },
    match: spec.match ?? "prefix",
  };
  // Lockable items carry the owning club id so the renderer can resolve that
  // club's subscription snapshot for the padlock ([NAV-LOCK-DISPLAY]).
  if (spec.restrictedWhenInactive) {
    node.subscriptionLock = true;
    node.clubId = club.id;
  }
  return node;
}

function clubNode(auth: NavAuthContext, club: NavClubInput, idPrefix: string, options: NavModelOptions): NavNode {
  const nodeId = `${idPrefix}:club:${club.shortName}`;
  return {
    id: nodeId,
    kind: "club",
    label: club.name,
    icon: mdiDomain,
    to: `/club/${club.shortName}`,
    expandable: true,
    locationLabel: club.locationLabel,
    clubId: club.id,
    clubShortName: club.shortName,
    children: clubChildren(auth, club, nodeId, options),
  };
}

// --- District sub-items (mirrors `districtNavItems`, MINUS route gate) ---

/**
 * COT is NOT permission-gated at all: the page is a public calendar with
 * registration links, so it hangs off every district node - member, visitor or
 * guest. The rest of the district sub-menu stays officer-only. The id follows
 * the node's prefix so a visiting district and a "My Clubs" district for the
 * same district number don't collide.
 */
/** Every route that sits under the district COT page in the sidebar. */
export const DISTRICT_COT_ROUTE_NAMES: readonly string[] = [
  "district-cot",
  "district-cot-management",
  "district-cot-detail",
  "district-cot-event-detail",
  "district-cot-session-detail",
  "district-cot-club-summary",
  "district-cot-unmatched",
  "cot-event-detail",
  "cot-event-register",
];

function districtCotChild(districtId: string, idPrefix: string): NavNode {
  return {
    id: `${idPrefix}:cot`,
    kind: "link",
    label: "COT",
    icon: mdiSchoolOutline,
    to: { name: "district-cot", params: { districtId } },
    activeRouteNames: DISTRICT_COT_ROUTE_NAMES,
  };
}

function districtChildren(auth: NavAuthContext, districtId: string, idPrefix: string): NavNode[] {
  if (!canManageDistrict(auth, districtId)) return [districtCotChild(districtId, idPrefix)];
  return [
    {
      id: `district:${districtId}:overview`,
      kind: "link",
      label: "Overview",
      icon: mdiHome,
      to: { name: "DistrictManagement", params: { districtId } },
      match: "exact",
    },
    {
      id: `district:${districtId}:upload`,
      kind: "link",
      label: "Upload Data",
      icon: mdiUploadOutline,
      to: { name: "DistrictUploadData", params: { districtId } },
    },
    {
      id: `district:${districtId}:email-lists`,
      kind: "link",
      label: "Email Lists",
      icon: mdiEmailOutline,
      to: { name: "DistrictMailingLists", params: { districtId } },
    },
    {
      id: `district:${districtId}:email-provider`,
      kind: "link",
      label: "Email Provider",
      icon: mdiSync,
      to: { name: "DistrictEmailProvider", params: { districtId } },
    },
    districtCotChild(districtId, idPrefix),
    {
      id: `district:${districtId}:settings`,
      kind: "link",
      label: "Settings",
      icon: mdiCogOutline,
      to: { name: "DistrictConfig", params: { districtId } },
    },
  ];
}

function divisionNodes(districtId: string, idPrefix: string, divisions: NavDivisionInput[]): NavNode[] {
  return divisions.map((div) => ({
    id: `${idPrefix}:division:${div.id}`,
    kind: "division" as const,
    label: `Division ${div.id}`,
    icon: mdiMapMarkerOutline,
    to: `/dashboard/district/${districtId}/division/${div.id}`,
    children: div.areas.map((area) => ({
      id: `${idPrefix}:division:${div.id}:area:${area.id}`,
      kind: "area" as const,
      label: `Area ${area.id}`,
      icon: mdiMapMarkerOutline,
      to: `/dashboard/district/${districtId}/division/${div.id}/area/${area.id}`,
    })),
  }));
}

/**
 * A district node for the "My Clubs" section: division/area context, then the
 * district sub-menu (COT for everyone, the rest permission-gated and
 * route-independent), then the club list.
 */
function myClubsDistrictNode(auth: NavAuthContext, district: NavDistrictInput, options: NavModelOptions): NavNode {
  const idPrefix = `district:${district.id}`;
  return {
    id: idPrefix,
    kind: "district",
    label: `District ${district.id}`,
    icon: mdiOfficeBuildingOutline,
    to: `/dashboard/district/${district.id}`,
    expandable: true,
    children: [
      ...divisionNodes(district.id, idPrefix, district.divisions),
      ...districtChildren(auth, district.id, idPrefix),
      ...district.clubs.map((club) => clubNode(auth, club, idPrefix, options)),
    ],
  };
}

/**
 * The visiting district node. Unlike "My Clubs" districts, it never carries the
 * officer sub-menu (visiting is a read-only context, matching today's UI). COT
 * is not part of that menu: the calendar is public, so a visitor gets it too.
 */
function visitingSection(
  auth: NavAuthContext,
  district: NavDistrictInput,
  visitingClub: NavClubInput | null,
  options: NavModelOptions,
): NavNode {
  const idPrefix = `visiting:district:${district.id}`;
  const children: NavNode[] = [
    ...divisionNodes(district.id, idPrefix, district.divisions),
    districtCotChild(district.id, idPrefix),
  ];
  if (visitingClub) {
    children.push(clubNode(auth, visitingClub, idPrefix, options));
  }
  const districtNode: NavNode = {
    id: idPrefix,
    kind: "district",
    label: `District ${district.id}`,
    icon: mdiOfficeBuildingOutline,
    to: `/dashboard/district/${district.id}`,
    expandable: true,
    children,
  };
  return { id: "section:visiting", kind: "section", label: "Visiting", children: [districtNode] };
}

export function buildNavModel(inputs: BuildNavModelInputs): NavNode[] {
  const { auth, districts, visitingDistrict, visitingClub } = inputs;
  const options: NavModelOptions = {
    subscriptionFeatureEnabled: inputs.options?.subscriptionFeatureEnabled ?? false,
    forcePersonalDashboard: inputs.options?.forcePersonalDashboard ?? false,
  };

  const nodes: NavNode[] = [];

  if (auth.isAuthenticated) {
    nodes.push({
      id: "home",
      kind: "link",
      label: "Home",
      icon: mdiViewDashboardOutline,
      to: options.forcePersonalDashboard
        ? { path: "/home", query: { [FORCED_PERSONAL_DASHBOARD_QUERY_PARAM]: "1" } }
        : "/home",
      match: "exact",
    });
  }
  nodes.push({ id: "find-a-club", kind: "link", label: "Find a Club", icon: mdiMagnify, to: "/findaclub" });

  if (visitingDistrict) {
    nodes.push(visitingSection(auth, visitingDistrict, visitingClub, options));
  }

  if (auth.isAuthenticated) {
    nodes.push({
      id: "section:my-clubs",
      kind: "section",
      label: "My Clubs",
      children: districts.map((district) => myClubsDistrictNode(auth, district, options)),
    });
  }

  return nodes;
}
